import * as THREE from "three";
import { config } from "../config.js";

// 大物(母艦・小母艦・大玉)のチャージ主砲の見た目(sim は src/sim/world.js の stepCannon。値は config.enemies.cannon)。world を見て描くだけ。
// 2026-11-01 オーナー要望「わかりやすくチャージしてから撃つ。じっとしていると食らうが、見ていれば避けられる」:
//   - チャージ中: 砲口に光の玉がふくらむ。狙いの線と、自機の面(z=0)の狙いの黄色い輪(当たる範囲)。狙いを追っている間は点滅、固まったら点きっぱなしで太くなる
//   - 撃っている間: 砲口から狙いを通って後ろまで、太い光の柱
const MAX = 4; // 同時に描く大物の数

export function createCannonView(scene) {
  const add = { blending: THREE.AdditiveBlending, transparent: true, depthWrite: false };
  const mk = (geo, color, opacity = 1) => {
    const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, opacity, ...add, side: THREE.DoubleSide }));
    m.frustumCulled = false;
    m.visible = false;
    scene.add(m);
    return m;
  };
  const cyl = new THREE.CylinderGeometry(1, 1, 1, 20, 1, true);
  const pool = [];
  for (let i = 0; i < MAX; i++) {
    pool.push({
      orb: mk(new THREE.SphereGeometry(1, 20, 14), 0xff7a3a),
      orbCore: mk(new THREE.SphereGeometry(1, 16, 10), 0xfff0d0),
      aim: mk(cyl, 0xffb020, 0.9), // 敵の弾(赤い玉と輪)と見分けるため、狙いは黄色
      ring: mk(new THREE.RingGeometry(0.8, 1, 48), 0xffc030, 0.9),
      beam: mk(cyl, 0xff5a20, 0.85),
      core: mk(cyl, 0xfff4e0),
    });
  }
  const up = new THREE.Vector3(0, 1, 0);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const d = new THREE.Vector3();

  // from → to の向きに、長さ1・半径1の筒を伸ばす
  function stretch(mesh, from, to, r) {
    d.subVectors(to, from);
    const len = d.length() || 1;
    mesh.position.addVectors(from, to).multiplyScalar(0.5);
    mesh.quaternion.setFromUnitVectors(up, d.divideScalar(len));
    mesh.scale.set(r, len, r);
    mesh.visible = true;
  }

  function update(world, now) {
    const T = config.enemies.types;
    let n = 0;
    for (const e of world.enemies) {
      if (!e.cc || e.hp <= 0 || n >= MAX) continue;
      const t = T[e.type];
      const c = { ...config.enemies.cannon, ...t.cannon };
      const v = pool[n++];
      for (const m of Object.values(v)) m.visible = false;
      const w = c.halfWidth * e.reach; // 当たる範囲の半径(自機の面で)
      const hitR = w + config.player.radius;
      a.set(e.x, e.y, e.z + (t.muzzleZ || 0) * e.size); // 砲口
      b.set(e.cc.ax, e.cc.ay, 0); // 狙い
      // 狙いの先(自機より後ろ)まで伸ばす
      const far = b.clone().sub(a).multiplyScalar(1 + (60 * world.scale) / Math.max(1, -a.z)).add(a);
      const firing = e.cc.t >= c.charge;
      if (!firing) {
        const k = e.cc.t / c.charge; // 0〜1
        const locked = e.cc.t >= c.charge - c.lock;
        const pulse = 1 + Math.sin(now / 50) * 0.12;
        const r = w * (0.4 + 1.4 * k) * pulse;
        v.orb.position.copy(a);
        v.orb.scale.setScalar(r);
        v.orb.material.opacity = 0.35 + 0.5 * k;
        v.orb.visible = true;
        v.orbCore.position.copy(a);
        v.orbCore.scale.setScalar(r * 0.5);
        v.orbCore.visible = true;
        // 狙っている間は点滅(だんだん速く)、固まったら点きっぱなし
        const on = locked || Math.floor(now / (170 - 110 * k)) % 2 === 0;
        if (on) {
          stretch(v.aim, a, b, (locked ? 0.9 : 0.4) * Math.sqrt(world.scale));
          v.aim.material.opacity = locked ? 1 : 0.6;
          v.ring.position.copy(b);
          v.ring.scale.setScalar(hitR * (locked ? 1 : 1.15));
          v.ring.material.opacity = locked ? 1 : 0.55;
          v.ring.visible = true;
        }
      } else {
        const k = (e.cc.t - c.charge) / c.fire; // 0〜1
        const flick = 1 + Math.sin(now / 25) * 0.1;
        const r = w * (k < 0.15 ? k / 0.15 : 1 - Math.max(0, k - 0.7) / 0.3) * flick;
        stretch(v.beam, a, far, Math.max(0.01, r));
        stretch(v.core, a, far, Math.max(0.01, r * 0.4));
        v.orb.position.copy(a);
        v.orb.scale.setScalar(w * 1.6 * flick);
        v.orb.material.opacity = 0.8;
        v.orb.visible = true;
      }
    }
    for (let i = n; i < MAX; i++) for (const m of Object.values(pool[i])) m.visible = false;
  }

  return { update };
}
